import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [coachProfile, setCoachProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      else {
        setProfile(null);
        setCoachProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId) => {
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      setProfile(profileData);

      // All roles can have a coach profile for their own business
      const { data: coachData } = await supabase
        .from('coach_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      setCoachProfile(coachData || null);
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setCoachProfile(null);
  };

  // Role helpers
  // Hierarchy: super_admin (company) > partner > senior_coach > coach > client
  const isOwner = profile?.role === 'super_admin'; // company / master account
  const isPartner = profile?.role === 'partner';
  const isSeniorCoach = profile?.role === 'senior_coach';
  const isCoach = profile?.role === 'coach';
  const isClient = profile?.role === 'client';
  // Admin-area access = company OR partner. isSuperAdmin kept as a
  // backwards-compat alias for "admin-level" (now includes the company tier).
  const isAdmin = isOwner || isPartner;
  const isSuperAdmin = isOwner || isPartner;

  const value = {
    user, profile, coachProfile, loading,
    isOwner, isPartner, isSeniorCoach, isCoach, isClient,
    isAdmin, isSuperAdmin, // kept for backwards compat
    signIn, signOut,
    refreshProfile: () => user && fetchProfile(user.id)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
